import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Toggle } from '@tes/ui';
import { api } from '../lib/api';
import { messageOf } from '../lib/errors';
import { useSettings } from '../lib/useSettings';
import { VariableLibraryPanel } from '../components/variables';
import { OperationalSettingsForm } from '../components/OperationalSettingsForm';
import { BrandingSettings } from '../components/BrandingSettings';
import { TutorialVideoSettings } from '../components/TutorialVideoSettings';
import { DocumentOrderRules } from '../components/DocumentOrderRules';
import type { PlatformSettings } from '../lib/types';

export default function SettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useSettings();

  const { mutate, isPending, error: saveError } = useMutation({
    mutationFn: (body: { signingMethods: Partial<PlatformSettings['signingMethods']> }) =>
      api.patchSettings(body),
    onSuccess: (updated) => {
      qc.setQueryData(['settings'], updated);
    },
  });

  const sm = data?.signingMethods;
  const enabledCount = sm ? [sm.allowDraw, sm.allowType, sm.allowUpload].filter(Boolean).length : 3;

  const toggle = (key: keyof PlatformSettings['signingMethods'], value: boolean) => {
    if (!value && enabledCount <= 1) return; // prevent disabling the last method
    mutate({ signingMethods: { [key]: value } });
  };

  const methodRow = 'border-b border-slate-100 py-2 last:border-0';

  return (
    <div>
      <h1 className="mb-5 text-lg font-semibold">Platform settings</h1>

      {error && <p className="mb-3 text-sm text-red-600">{messageOf(error, 'Could not load settings')}</p>}

      {isLoading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : data ? (
        <div className="space-y-6">
          <OperationalSettingsForm settings={data} />
          <DocumentOrderRules rules={data.documentOrder.keywordRules} />
          <section className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-700">Allow signing by</h2>
              {isPending && <span className="text-xs text-slate-400">Saving…</span>}
            </div>
            {saveError && (
              <p className="mb-2 text-sm text-red-600">{messageOf(saveError, 'Save failed')}</p>
            )}
            <Toggle
              className={methodRow}
              label="Drawing"
              description="Signer draws their signature with a finger or mouse"
              checked={data.signingMethods.allowDraw}
              disabled={isPending || (data.signingMethods.allowDraw && enabledCount <= 1)}
              onChange={(v) => toggle('allowDraw', v)}
            />
            <Toggle
              className={methodRow}
              label="Typing"
              description="Signer types their name and it's rendered as a cursive signature"
              checked={data.signingMethods.allowType}
              disabled={isPending || (data.signingMethods.allowType && enabledCount <= 1)}
              onChange={(v) => toggle('allowType', v)}
            />
            <Toggle
              className={methodRow}
              label="Upload"
              description="Signer uploads a PNG or JPEG image of their signature"
              checked={data.signingMethods.allowUpload}
              disabled={isPending || (data.signingMethods.allowUpload && enabledCount <= 1)}
              onChange={(v) => toggle('allowUpload', v)}
            />
            {enabledCount <= 1 && (
              <p className="mt-2 text-xs text-amber-600">At least one method must remain enabled.</p>
            )}
          </section>

          <BrandingSettings />
          <TutorialVideoSettings branding={data.branding} />
          <section className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="mb-3 text-sm font-semibold text-slate-700">Variable library</h2>
            <VariableLibraryPanel />
          </section>
        </div>
      ) : null}
    </div>
  );
}
